import { createStyles, Stack } from '@mantine/core';
import { Banner } from '@repo/foundations';
import type { BannerTones } from '@repo/foundations/components/Banner/Banner';
import { buttonHoverTransition } from '@repo/theme/primitives';

interface SettingsBannerProps {
	message: string;
	tone?: BannerTones;
}

const useStyles = createStyles(
	(
		theme,
		{
			hasOnClickRow,
			variant,
		}: { hasOnClickRow: boolean; variant: 'primary' | 'tertiary' }
	) => ({
		wrapper: {
			cursor: 'default',
			gap: 0,
			justifyContent: 'center',
			paddingTop: theme.spacing.sm, // Exact 12px padding top
			paddingBottom: theme.spacing.sm, // Exact 12px padding bottom
			'&:hover .right-component-hover': {
				opacity: 1,
			},
			'&:not(:hover) .right-component-hover': {
				opacity: 0,
			},
			paddingLeft: theme.spacing.md,
			paddingRight: theme.spacing.md,
			backgroundColor:
				variant === 'tertiary'
					? theme.other.getColor('surface/secondary/default')
					: theme.other.getColor('surface/primary/default'),
			borderRadius: theme.radius.md,
			border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			...buttonHoverTransition(),
			minHeight: theme.other.space[10] * 1.5, // Ensure minimum height of 60px
			boxSizing: 'border-box', // Ensure padding is included in height calculation
			...(hasOnClickRow && {
				'&:hover': {
					backgroundColor: theme.other.getColor('surface/primary/hover'),
				},
			}),
			'&:not(:last-child)': {
				borderBottom: 'none',
				borderBottomLeftRadius: 0,
				borderBottomRightRadius: 0,
				position: 'relative',
				'&::after': {
					content: '""',
					position: 'absolute',
					bottom: 0,
					left: theme.spacing.md,
					right: theme.spacing.md,
					height: '1px',
					backgroundColor: theme.other.getColor('border/secondary/default'),
				},
			},
			'&:not(:first-of-type)': {
				borderTopLeftRadius: 0,
				borderTopRightRadius: 0,
				borderTop: 'none',
			},
		},
	})
);

export function SettingsBanner({
	message,
	tone = 'info',
}: SettingsBannerProps) {
	const { classes } = useStyles({ hasOnClickRow: false, variant: 'primary' });

	return (
		<Stack className={classes.wrapper}>
			<Banner tone={tone} message={message} inCard />
		</Stack>
	);
}
